import { Button } from '@bambi/component-library/src/components/Button';
import { ContextFormFieldText } from '@bambi/component-library/src/components/forms/context/ContextFormFieldText';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { useAppDispatch } from 'src/app/store/hooks';
import type { PayerPassengerRead } from 'src/common/external/bambi-api/bambiApi';
import { usePayersPassengersPartialUpdateMutation } from 'src/common/external/bambi-api/bambiApi';
import { Modal } from 'src/common/primitives/Modal/Modal';
import { ModalTitle } from 'src/common/primitives/Modal/ModalTitle';
import { show } from 'src/common/primitives/Toast/toast.slice';
import formatServerError from 'src/common/util/serverErrorFormatter';

interface FormValues {
  external_id: string;
  authorization_id: string;
}

interface PayerPassengerEditModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onClose: (saved: boolean) => void;
  payerPassenger: PayerPassengerRead;
}

export function PayerPassengerEditModal({
  open,
  setOpen,
  onClose,
  payerPassenger,
}: PayerPassengerEditModalProps) {
  const dispatch = useAppDispatch();
  const [updatePayerPassenger] = usePayersPassengersPartialUpdateMutation();

  const payerType = payerPassenger.payer_organization.payer_type;
  const isInsurance = payerType === 'insurance';

  const methods = useForm<FormValues>({
    defaultValues: {
      external_id: payerPassenger.external_id ?? '',
      authorization_id: payerPassenger.authorization_id ?? '',
    },
  });
  const { handleSubmit, reset } = methods;

  useEffect(() => {
    reset({
      external_id: payerPassenger.external_id ?? '',
      authorization_id: payerPassenger.authorization_id ?? '',
    });
  }, [payerPassenger, reset]);

  const handleClose = (saved: boolean) => {
    reset();
    setOpen(false);
    onClose(saved);
  };

  const onSubmit = handleSubmit(async (data) => {
    try {
      await updatePayerPassenger({
        passengerId: payerPassenger.passenger_id,
        payerOrganizationId: payerPassenger.payer_organization_id,
        patchedPayerPassenger: {
          external_id: data.external_id,
          ...(isInsurance && {
            authorization_id: data.authorization_id || null,
          }),
        },
      }).unwrap();
      dispatch(
        show({
          title: 'Payer passenger updated',
          description: 'The payer passenger information has been updated.',
          type: 'success',
        })
      );
      handleClose(true);
    } catch (e) {
      dispatch(
        show({
          title: 'Failed to update payer passenger',
          description: formatServerError(e),
          type: 'error',
        })
      );
    }
  });

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      onClose={() => handleClose(false)}
      contentClassnames="w-[400px]"
      title="Edit Payer Passenger"
      testId="payer-passenger-edit-modal"
    >
      <div className="flex flex-col gap-4">
        <ModalTitle>
          {payerPassenger.payer_organization.display_name}
        </ModalTitle>
        <FormProvider {...methods}>
          <div className="flex flex-col gap-3">
            <ContextFormFieldText
              fieldPath="external_id"
              label={isInsurance ? 'Insurance Policy ID' : 'Member ID'}
            />
            {isInsurance && (
              <ContextFormFieldText
                fieldPath="authorization_id"
                label="Authorization ID"
              />
            )}
          </div>
        </FormProvider>
      </div>
      <div className="mt-4 flex flex-row justify-between">
        <Button variant="secondary" onClick={() => handleClose(false)}>
          Cancel
        </Button>
        <Button variant="primary" onClick={onSubmit}>
          Save
        </Button>
      </div>
    </Modal>
  );
}
